/**
 * initAgents()
 * ------------------------------------------------------------
 * 目的:
 * - ブラウザ/OS 依存の崩れが出たとき、即座に限定CSSを当てられるように
 *   <html>（document.documentElement）へ判定クラスを付与する。
 *
 * 付与されるクラス一覧:
 *
 * ■ OS（os-*）
 * - os-windows : Windows
 * - os-mac     : macOS
 * - os-linux   : Linux
 * - os-android : Android
 * - os-ios     : iOS / iPadOS
 *
 * ■ Browser（browser-*）
 * - browser-chrome  : Chrome（iOS は CriOS を含む）
 * - browser-safari  : Safari
 * - browser-firefox : Firefox（iOS は FxiOS を含む）
 * - browser-edge    : Edge（iOS は EdgiOS を含む）
 * - browser-opera   : Opera
 * - browser-ie      : IE（msie / trident）
 *
 * ■ Engine（engine-*）
 * - engine-blink  : Blink（Chrome / Edge / Opera 系）
 * - engine-webkit : WebKit（主に Safari / iOS 系）
 * - engine-gecko  : Gecko（Firefox 系）
 *
 * ■ Device feature（固定名）
 * - has-touch : タッチ入力が可能（maxTouchPoints > 0）
 *
 * 注意:
 * - iOS のブラウザ（Chrome/Firefox/Edge）は “見た目の名前” が違っても
 *   ほぼ WebKit ベースなので、実務では `.os-ios.engine-webkit` の方が効くことが多い。
 * - UA 判定は将来変更される可能性があるため、用途は「応急パッチ用途」に限定推奨。
 *
 * CSS 当て例:
 * - Safari(WebKit)だけ:        `.engine-webkit .foo { ... }`
 * - iOS(WebKit)に限定:         `.os-ios.engine-webkit .foo { ... }`
 * - Edge だけ:                 `.browser-edge .foo { ... }`
 */

export default function initAgents() {
	const run = () => {
		const root = document.documentElement; // <html>
		if (!root) return;

		const ua = navigator.userAgent || "";
		const uaL = ua.toLowerCase();

		// --- OS 判定（iOS の "like Mac OS X" 誤判定を避ける）
		const platform = (navigator.userAgentData?.platform || "").toLowerCase();
		const maxTouch = navigator.maxTouchPoints || 0;

		// iPadOS: platform が macOS っぽく見える場合があるので touch で補助
		const isIOS =
			/iphone|ipad|ipod/.test(uaL) ||
			(platform === "macos" && maxTouch > 1);

		const os =
			isIOS ? "ios" :
				/android/.test(uaL) ? "android" :
					/windows nt/.test(uaL) ? "windows" :
						/mac os x/.test(uaL) ? "mac" :
							/linux/.test(uaL) ? "linux" :
								null;

		if (os) root.classList.add(`os-${os}`);

		// --- Browser 判定（iOS 系識別も拾う）
		const browser =
			/msie|trident/.test(uaL) ? "ie" :
				/edg\//.test(uaL) || /edge\//.test(uaL) || /edgios/.test(uaL) ? "edge" :
					/opr\//.test(uaL) || /opera/.test(uaL) ? "opera" :
						/firefox/.test(uaL) || /fxios/.test(uaL) ? "firefox" :
							/chrome/.test(uaL) || /crios/.test(uaL) ? "chrome" :
								/safari/.test(uaL) ? "safari" :
									null;

		if (browser) root.classList.add(`browser-${browser}`);

		// --- Engine 判定（ブラウザ名より “崩れの原因” に直結することが多い）
		const engine =
			/gecko\//.test(uaL) && (/firefox|fxios/.test(uaL)) ? "gecko" :
				/applewebkit/.test(uaL) && !(/chrome|crios|edg|opr/.test(uaL)) ? "webkit" :
					/chrome|crios|edg|opr/.test(uaL) ? "blink" :
						null;

		if (engine) root.classList.add(`engine-${engine}`);

		// --- Device feature
		root.classList.toggle("has-touch", maxTouch > 0);
	};

	// 二重登録防止 + DOM 準備済みなら即実行
	if (document.readyState === "loading") {
		window.addEventListener("DOMContentLoaded", run, { once: true });
	} else {
		run();
	}
}
